import React, { useState, useEffect ,useMemo} from "react";
import { Box, Typography, useTheme, Paper, Dialog, DialogContent } from "@mui/material";
import SidebarLayout from "../../../shared/sections/sidebar/SidebarLayout";
import TitleSection from "../../../shared/sections/TitleSection";
import LeftNavigation from "../../../shared/sections/NavigationSection";
import InfoNotification from "../../../shared/modules/InfoNotification";
import QuickCostButton from "../../../shared/modules/QuickCostButton";
import CircularLoading from "../../../shared/modules/CircularLoading";
import ProgressiveAnalysisTracker from "../casting/ProgressiveAnalysisTracker";
import ResultsSection from "../../../shared/sections/ResultsSection";
import { SheetmetalQuestions } from "./questions";
import { SheetmetalPlayBook } from "./playbook";
import DescriptionIcon from "@mui/icons-material/Description";
import TuneIcon from "@mui/icons-material/Tune";
import AssessmentIcon from "@mui/icons-material/Assessment";
import { apiFetch } from "../../../api/apiClient";

const DEFAULT_BREAKDOWN_ORDER = ["material", "process", "packaging", "ohp"];
// const normalizeKey = (key = "") => key.toLowerCase().replace(/[\s_-]+/g, "");
const SKIP_BREAKDOWN_KEYS = new Set(["currency", "total_should_cost", "total", "logistics"]);
// const SKIP_BREAKDOWN_KEYS = new Set(["currency", "total_should_cost", "logistics","total"].map(normalizeKey));
// const ALLOWED_BREAKDOWN_KEYS = new Set(DEFAULT_BREAKDOWN_ORDER.map(normalizeKey));
const BREAKDOWN_LABELS = {
  material: "Material",
  process: "Process",
  packaging: "Packaging",
  ohp: "Overhead & Profits",
  logistics: "Logistics",
};
 
const toLabel = (key) =>
  (key || "")
    .replace(/_/g, " ")
    .replace(/\s+/g, " ")
    .trim()
    .replace(/\b\w/g, (c) => c.toUpperCase());
 
const buildCostRows = (breakdown = {}) => {
  const entries = Object.entries(breakdown).filter(([key, value]) => {
    // const normKey = normalizeKey(key);
    // if (SKIP_BREAKDOWN_KEYS.has(normKey)) return false;
    if (SKIP_BREAKDOWN_KEYS.has(key)) return false;
    // if (ALLOWED_BREAKDOWN_KEYS.size > 0 && !ALLOWED_BREAKDOWN_KEYS.has(normKey)) return false;
    return Number.isFinite(Number(value));
  });
 
  entries.sort((a, b) => {
    // const ai = DEFAULT_BREAKDOWN_ORDER.findIndex((k) => normalizeKey(k) === normalizeKey(a[0]));
    // const bi = DEFAULT_BREAKDOWN_ORDER.findIndex((k) => normalizeKey(k) === normalizeKey(b[0]));
    const ai = DEFAULT_BREAKDOWN_ORDER.findIndex((k) => k === a[0]);
    const bi = DEFAULT_BREAKDOWN_ORDER.findIndex((k) => k === b[0]);
    if (ai !== -1 && bi !== -1) return ai - bi;
    if (ai !== -1) return -1;
    if (bi !== -1) return 1;
    return a[0].localeCompare(b[0]);
  });
 
  return entries.map(([key, value]) => {
    // const normKey = normalizeKey(key);
    // const label = BREAKDOWN_LABELS[key] || BREAKDOWN_LABELS[normKey] || toLabel(key);
    const label = BREAKDOWN_LABELS[key] || toLabel(key);
    return {
      key,
      label,
      value: Number(value) || 0,
    };
  });
};


const buildSheetmetalAssumptions = (costResult, playbook, product, costBreakdown = null) => {
  const assumptions = Array.isArray(costResult?.assumptions)
    ? [...costResult.assumptions]
    : typeof costResult?.assumptions === "string"
    ? [costResult.assumptions]
    : [];

  const costParams = playbook?.costParams || playbook || {};
  const productInfo = product || {};
  const operations = productInfo.operations || {};
  const partDimensions = productInfo.partWeightDimensions || {};
  const materialSelection = productInfo.materialSelection;
  const materialGrade = productInfo.materialGrade || productInfo?.materialSelection?.materialGrade;

  if (materialSelection) {
    const formattedMaterial =
      typeof materialSelection === "object"
        ? materialSelection.material
        : materialSelection;
    const grade =
      typeof materialSelection === "object"
        ? materialSelection.materialGrade
        : materialGrade;
    assumptions.push(
      grade && grade !== ""
        ? `Material: ${formattedMaterial} (Grade: ${grade})`
        : `Material: ${formattedMaterial}`
    );
  }

  if (productInfo.processSelection) {
    assumptions.push(`Process: ${productInfo.processSelection}`);
  }

  if (
    partDimensions.finishWeight ||
    partDimensions.partLength ||
    partDimensions.partWidth ||
    partDimensions.partHeight
  ) {
    const weight = partDimensions.finishWeight ? `${partDimensions.finishWeight} g` : "N/A";
    const length = partDimensions.partLength ? `${partDimensions.partLength} mm` : "N/A";
    const width = partDimensions.partWidth ? `${partDimensions.partWidth} mm` : "N/A";
    const height = partDimensions.partHeight ? `${partDimensions.partHeight} mm` : "N/A";
    assumptions.push(
      `Part dimensions: weight ${weight}, L ${length}, W ${width}, H ${height}`
    );
  }

  // Use playbook values for slider-controlled fields (prioritize over backend values)
  const pressTonnage = costParams.pressTonnage;
  if (pressTonnage !== undefined && pressTonnage !== null && pressTonnage !== 0) {
    assumptions.push(`Tonnage: ${pressTonnage} Tons`);
  }

  // Cavities is always defaulted to 1 in backend, not shown in assumptions
  
  const deburrCycleTime = costParams.deburrCycleTime;
  if (deburrCycleTime !== undefined && deburrCycleTime !== null && deburrCycleTime !== 0) {
    const deburrCycleTimeValue = typeof deburrCycleTime === "number" ? deburrCycleTime : parseFloat(deburrCycleTime);
    if (!isNaN(deburrCycleTimeValue)) {
      assumptions.push(`Deburr Cycle Time: ${deburrCycleTimeValue.toFixed(2)} sec`);
    }
  }

  if (costParams.dieType && costParams.dieType !== "N/A") {
    assumptions.push(`Die type: ${costParams.dieType}`);
  }

  // Add OHP value from cost breakdown if available
  if (costBreakdown?.ohp !== undefined && costBreakdown?.ohp !== null) {
    const ohpValue = typeof costBreakdown.ohp === "number" 
      ? costBreakdown.ohp 
      : parseFloat(costBreakdown.ohp);
    if (!isNaN(ohpValue) && ohpValue > 0) {
      assumptions.push(`Overhead & Profits: $${ohpValue.toFixed(4)}/unit`);
    }
  }

  // Cavities is always defaulted to 1 in backend, not shown in assumptions

  if (operations.operationsCount) {
    assumptions.push(`No. of operations: ${operations.operationsCount}`);
  }

  // Add attributes from cost result (costResult is already the cost row object)
  const costRow = Array.isArray(costResult) && costResult.length > 0 
    ? costResult[0] 
    : (typeof costResult === "object" ? costResult : {});
  
  // Cycle Time: Use stored procedure output [CycleTime_sec]
  if (costRow.CycleTime_sec !== null && 
      costRow.CycleTime_sec !== undefined && 
      costRow.CycleTime_sec !== "") {
    const cycleTimeValue = typeof costRow.CycleTime_sec === "number" 
      ? costRow.CycleTime_sec 
      : parseFloat(costRow.CycleTime_sec);
    if (!isNaN(cycleTimeValue)) {
      assumptions.push(`Cycle Time(sec): ${cycleTimeValue.toFixed(2)} sec`);
    }
  }
  
  // Helper function to format numeric values
  const formatValue = (value, unit = "") => {
    if (value === null || value === undefined || value === "") return null;
    const numValue = typeof value === "number" ? value : parseFloat(value);
    if (isNaN(numValue)) return null;
    // Format with appropriate decimal places
    const formatted = numValue % 1 === 0 ? numValue.toString() : numValue.toFixed(2);
    return `${formatted}${unit ? ` ${unit}` : ""}`;
  };

  // Only show backend values for fields not controlled by sliders
  // (Slider-controlled fields are already shown above using playbook values)
  
  // Material Price: MaterialPrice_perKg
  if (costRow.MaterialPrice_perKg !== null && 
      costRow.MaterialPrice_perKg !== undefined && 
      costRow.MaterialPrice_perKg !== "") {
    const materialPriceValue = formatValue(costRow.MaterialPrice_perKg, "$/kg");
    if (materialPriceValue) {
      assumptions.push(`Material Price: ${materialPriceValue}`);
    }
  }

  // Labour Rate: LabourRate_perHr_Used
  if (costRow.LabourRate_perHr_Used !== null && 
      costRow.LabourRate_perHr_Used !== undefined && 
      costRow.LabourRate_perHr_Used !== "") {
    const labourRateValue = formatValue(costRow.LabourRate_perHr_Used, "$/hr");
    if (labourRateValue) {
      assumptions.push(`Labour Rate: ${labourRateValue}`);
    }
  }

  return assumptions;
};

// Transform backend response array to cost breakdown format
const transformSheetMetalCostResult = (costResult) => {
  if (!costResult) return null;

  // Backend returns an array from QC_SheetMetal_Output table
  let costRow = null;
  if (Array.isArray(costResult) && costResult.length > 0) {
    costRow = costResult[0];
  } else if (typeof costResult === "object" && !Array.isArray(costResult)) {
    costRow = costResult;
  } else {
    return null;
  }

  // Helper to safely parse numeric values
  const to4 = (n) => {
    if (n === null || n === undefined || n === '') return 0;
    if (typeof n === 'string') {
      const cleaned = n.replace(/[$,\s]/g, '').trim();
      const parsed = parseFloat(cleaned);
      return isNaN(parsed) ? 0 : Number(Number(parsed).toFixed(4));
    }
    return Number(Number(n).toFixed(4));
  };

  // Map backend fields to breakdown format
  // Material: Map MaterialCost_USD (material cost per unit) to material field
  // MaterialPrice_perKg is also available in the raw data for reference
  // Prefer MaterialCost_USD from the SP output; fall back to other keys only if missing/null
  const materialSource =
    costRow.MaterialCost_USD ??
    costRow.MaterialCost ??
    costRow.materialCost ??
    0;
  const material = to4(materialSource);

  // Process: Total machine + labour cost
  const processSource =
    costRow.Total_MachinePlusLabour ??
    costRow.TotalMachinePlusLabour ??
    costRow.Cost_Main ??
    costRow.ProcessCost ??
    0;
  const process = to4(processSource);

  // OHP: Overheads and profits
  // Prefer Overheads_Profits from the SP output; fall back to other keys only if missing/null
  // Also check case-insensitive variations
  const ohpKeys = Object.keys(costRow).filter(k => 
    k.toLowerCase().includes('overhead') || 
    k.toLowerCase().includes('ohp') || 
    (k.toLowerCase().includes('profit') && !k.toLowerCase().includes('percentage'))
  );
  console.log("🔍 [SheetmetalPage] OHP fields from stored procedure:", {
    Overheads_Profits: costRow.Overheads_Profits,
    OverheadsProfits: costRow.OverheadsProfits,
    OHP: costRow.OHP,
    allOHPKeys: ohpKeys,
    allOHPValues: ohpKeys.reduce((acc, k) => ({ ...acc, [k]: costRow[k] }), {})
  });
  
  // Try exact matches first, then case-insensitive
  let ohpSource = 
    costRow.Overheads_Profits ??
    costRow.OverheadsProfits ??
    costRow.OHP;
  
  // If not found, try case-insensitive search
  if (ohpSource === null || ohpSource === undefined) {
    const ohpKey = ohpKeys.find(k => 
      k.toLowerCase() === 'overheads_profits' || 
      k.toLowerCase() === 'overheadsprofits' ||
      k.toLowerCase() === 'ohp'
    );
    if (ohpKey) {
      ohpSource = costRow[ohpKey];
    }
  }
  
  ohpSource = ohpSource ?? 0;
  const ohp = to4(ohpSource);
  console.log("🔍 [SheetmetalPage] OHP value after mapping:", ohp, "from source:", ohpSource);

  // Calculate total
  const total = Number((material + process + ohp).toFixed(4));

  // Get final price if available
  const finalPrice = to4(costRow.Final_Price_USD || costRow.FinalPrice_USD || 0);

  return {
    ...costRow,
    breakdown: {
      material: material,
      process: process,
      ohp: ohp,
      total_should_cost: finalPrice || total,
      currency: "USD/unit",
    },
    summary: `Total cost: $${(finalPrice || total).toFixed(4)} per unit`,
  };
};

const enhanceCostResultWithAssumptions = (costResult, playbook, product) => {
  // Transform the backend response first
  const transformedResult = transformSheetMetalCostResult(costResult);
  
  if (!transformedResult) {
    return costResult;
  }

  // Get the cost row for assumptions (original data)
  const costRow = Array.isArray(costResult) && costResult.length > 0 
    ? costResult[0] 
    : (typeof costResult === "object" ? costResult : {});

  return {
    ...transformedResult,
    assumptions: buildSheetmetalAssumptions(costRow, playbook, product, transformedResult.breakdown),
    _originalCostRow: costRow, // Store original cost row for rebuilding assumptions
  };
};

// Handling assumption section locally and rendering the component here
function renderAssumptions({
  assumptions=[] ,
  productData,
  perlColors,
  perlTypography,
}){

  return (
  <Box>
    {assumptions && assumptions.length > 0 && (
              <Paper
                sx={{
                  p: 3,
                  bgcolor: perlColors.surface,
                  border: `1px solid ${perlColors.border}`,
                  borderRadius: 2,
                  mt: 3,
                }}
              >
                {(() => {
                  const bottomSectionKeywords = [
                    "Machine tonnage:",
                    "Tonnage:",
                    "Cycle time:",
                    "Cycle Time(sec):",
                    "Machine rate:",
                    "OHP rate:",
                    "No. of operations:",
                    "Material price:",
                    "Material Price:",
                    "Labour cost:",
                    "Labour Rate:",
                    "Deburr Cycle Time:",
                    "Die type:",
                    "Overhead & Profits:",
                  ];

                  // Case-insensitive matching for keywords
                  const topAssumptions = assumptions.filter(
                    (assumption) =>
                      !bottomSectionKeywords.some((keyword) =>
                        assumption.toLowerCase().startsWith(keyword.toLowerCase())
                      )
                  );
                  const bottomAssumptions = assumptions.filter((assumption) =>
                    bottomSectionKeywords.some((keyword) =>
                      assumption.toLowerCase().startsWith(keyword.toLowerCase())
                    )
                  );

                  // Build product information from productData if available
                  const buildProductInfo = () => {
                    if (!productData) {
                      // Fallback to topAssumptions if productData not available
                      return topAssumptions.map((assumption) => assumption);
                    }

                    const productInfo = [];

                    // Product Name
                    if (productData.productName) {
                      productInfo.push(`Product Name: ${productData.productName}`);
                    }

                    // Material
                    if (productData.materialSelection) {
                      const formattedMaterial =
                        typeof productData.materialSelection === "object"
                          ? productData.materialSelection.material
                          : productData.materialSelection;
                      const grade =
                        typeof productData.materialSelection === "object"
                          ? productData.materialSelection.materialGrade
                          : productData.materialGrade;
                      productInfo.push(
                        grade && grade !== ""
                          ? `Material: ${formattedMaterial} (Grade: ${grade})`
                          : `Material: ${formattedMaterial}`
                      );
                    }

                    // Process
                    if (productData.processSelection) {
                      productInfo.push(`Process: ${productData.processSelection}`);
                    }

                    // Part Dimensions
                    const dims = productData.partWeightDimensions;
                    if (dims) {
                      const dimParts = [];
                      if (dims.partLength) dimParts.push(`L: ${dims.partLength}mm`);
                      if (dims.partWidth) dimParts.push(`W: ${dims.partWidth}mm`);
                      if (dims.partHeight) dimParts.push(`H: ${dims.partHeight}mm`);
                      if (dims.finishWeight) dimParts.push(`Weight: ${dims.finishWeight}g`);
                      if (dimParts.length > 0) {
                        productInfo.push(`Part Dimensions: ${dimParts.join(", ")}`);
                      }
                    }

                    // Manufacturing Country
                    if (productData.manufacturingCountry) {
                      productInfo.push(
                        `Manufacturing Country: ${productData.manufacturingCountry}`
                      );
                    }

                    // Annual Volume
                    if (productData.annualVolume) {
                      productInfo.push(`Annual Volume: ${productData.annualVolume}`);
                    }

                    // Operations Details
                    const operations = productData.operations || {};
                    if (operations.operationsCount) {
                      productInfo.push(`No. of Operations: ${operations.operationsCount}`);
                    }
                    if (operations.timePerBend) {
                      productInfo.push(`Time per Bend: ${operations.timePerBend}s`);
                    }
                    if (operations.deburringEnabled && operations.deburringEnabled !== "No") {
                      productInfo.push(`Deburring: ${operations.deburringEnabled}`);
                      if (operations.deburringCycleTime) {
                        productInfo.push(`Deburring Cycle Time: ${operations.deburringCycleTime}s`);
                      }
                    }
                    if (operations.noOfBendsPressBrake) {
                      productInfo.push(`No. of Bends (Press Brake): ${operations.noOfBendsPressBrake}`);
                    }
                    if (operations.noOfBends) {
                      productInfo.push(`No. of Bends: ${operations.noOfBends}`);
                    }
                    if (operations.noOfTapsHoles) {
                      productInfo.push(`No. of Taps/Holes: ${operations.noOfTapsHoles}`);
                    }
                    if (operations.weldingLength) {
                      productInfo.push(`Welding Length: ${operations.weldingLength}mm`);
                    }

                    // Surface Finish Details
                    const surfaceFinish = productData.surfaceFinish || {};
                    if (surfaceFinish.paintingMaterial) {
                      const paintingParts = [`Painting Material: ${surfaceFinish.paintingMaterial}`];
                      if (surfaceFinish.paintingSurfaceArea) {
                        paintingParts.push(`Surface Area: ${surfaceFinish.paintingSurfaceArea}mm²`);
                      }
                      if (surfaceFinish.paintingThickness) {
                        paintingParts.push(`Thickness: ${surfaceFinish.paintingThickness}mm`);
                      }
                      productInfo.push(paintingParts.join(", "));
                    }
                    if (surfaceFinish.platingMaterial) {
                      const platingParts = [`Plating Material: ${surfaceFinish.platingMaterial}`];
                      if (surfaceFinish.platingSurfaceArea) {
                        platingParts.push(`Surface Area: ${surfaceFinish.platingSurfaceArea}mm²`);
                      }
                      if (surfaceFinish.platingThickness) {
                        platingParts.push(`Thickness: ${surfaceFinish.platingThickness}mm`);
                      }
                      productInfo.push(platingParts.join(", "));
                    }

                    // Add remaining topAssumptions that aren't already covered
                    const coveredKeywords = [
                      "product name:",
                      "material:",
                      "process:",
                      "part dimensions:",
                      "manufacturing country:",
                      "annual volume:",
                      "no. of operations:",
                      "time per bend:",
                      "deburring:",
                      "deburring cycle time:",
                      "no. of bends (press brake):",
                      "no. of bends:",
                      "no. of taps/holes:",
                      "welding length:",
                      "painting material:",
                      "plating material:",
                    ];
                    const remainingAssumptions = topAssumptions.filter((assumption) => {
                      const lowerAssumption = assumption.toLowerCase().trim();
                      return !coveredKeywords.some((keyword) =>
                        lowerAssumption.startsWith(keyword)
                      );
                    });

                    return [...productInfo, ...remainingAssumptions];
                  };

                  const productInfoItems = buildProductInfo();

                  return (
                    <Box sx={{ display: "flex", gap: 3 }}>
                      <Box sx={{ flex: 1 }}>
                        <Typography
                          variant="body2"
                          sx={{
                            mb: 2,
                            fontSize: "14px",
                            fontWeight: 600,
                            lineHeight: "18px",
                            fontFamily: perlTypography.family,
                            letterSpacing: "0.3px",
                            color: perlColors.textPrimary,
                          }}
                        >
                          Product Information
                        </Typography>
                        <Box component="ul" sx={{ m: 0, pl: 2.5 }}>
                          {productInfoItems.map((item, idx) => (
                            <Typography
                              key={idx}
                              component="li"
                              variant="body2"
                              sx={{
                                mb: 1,
                                fontSize: "14px",
                                fontFamily: perlTypography.family,
                                color: perlColors.textSecondary,
                                lineHeight: "20px",
                                letterSpacing: "0.3px",
                              }}
                            >
                              {item}
                            </Typography>
                          ))}
                        </Box>
                      </Box>

                      {bottomAssumptions.length > 0 && (
                        <Box
                          sx={{
                            width: "1px",
                            bgcolor: perlColors.border || "rgba(255, 255, 255, 0.12)",
                            alignSelf: "stretch",
                          }}
                        />
                      )}

                      {bottomAssumptions.length > 0 && (
                        <Box sx={{ flex: 1 }}>
                          <Typography
                            variant="body2"
                            sx={{
                              mb: 2,
                              fontSize: "14px",
                              fontWeight: 600,
                              lineHeight: "18px",
                              fontFamily: perlTypography.family,
                              letterSpacing: "0.3px",
                              color: perlColors.textPrimary,
                            }}
                          >
                            Assumptions
                          </Typography>
                          <Box component="ul" sx={{ m: 0, pl: 2.5 }}>
                            {bottomAssumptions.map((assumption, idx) => (
                              <Typography
                                key={idx}
                                component="li"
                                variant="body2"
                                sx={{
                                  mb: 1,
                                  fontSize: "14px",
                                  fontFamily: perlTypography.family,
                                  color: perlColors.textSecondary,
                                  lineHeight: "20px",
                                  letterSpacing: "0.3px",
                                }}
                              >
                                {assumption}
                              </Typography>
                            ))}
                          </Box>
                        </Box>
                      )}
                    </Box>
                  );
                })()}
              </Paper>
            )}

  </Box>
)
}

export default function CookiesPage() {
  const theme = useTheme();
  const perlColors = theme.custom?.perlColors || {
    background: "#191A1B",
    textPrimary: "#F1F2F3",
  };
  const perlSpacing = theme.custom?.perlSpacing || {};

  const [activeSection, setActiveSection] = useState("product");
  const [sectionStarted, setSectionStarted] = useState({
    product: false,
    formulation: false,
    playbook: false,
    results: false,
  });

  const [productData, setProductData] = useState(null);
  const [isProcessing, setIsProcessing] = useState(false);
  const [loadingMessage, setLoadingMessage] = useState("");
  const [recipeData, setRecipeData] = useState([]);
  const [playBookData, setPlayBookData] = useState(null);
  const [costData, setCostData] = useState(null);
  const [riskData, setRiskData] = useState(null);
  const [substituteData, setSubstituteData] = useState(null);
  const [productImageUrl, setProductImageUrl] = useState(null);
  const [keyInsightsData, setKeyInsightsData] = useState(null);
  // Store original cost breakdown for recalculation
  const [originalCostBreakdown, setOriginalCostBreakdown] = useState(null);
  const [originalTonnage, setOriginalTonnage] = useState(null);
  const [originalOhpRate, setOriginalOhpRate] = useState(null); // Store original OHP rate from stored procedure
  const [generatingImage, setGeneratingImage] = useState(false);
  const [demoMode, setDemoMode] = useState(false);

  // Progressive analysis tracker state
  const [showProgressTracker, setShowProgressTracker] = useState(false);
  const [progressSteps, setProgressSteps] = useState([]);
  const [currentProgressStep, setCurrentProgressStep] = useState(0);
  const [overallProgress, setOverallProgress] = useState(0);

  // Functional groups for formulation
  const functionalGroups = [
    "Bulk ingredient",
    "Sweetners",
    "Oil and fats",
    "Active ingredient",
    "Leavening agents",
    "Flavour enhancers",
    "Flavours",
    "Colours",
    "Preservatives",
    "Emulsifiers",
    "Stabilizers",
    "Thickeners",
    "Acidulants",
    "Vitamins and minerals",
    "Other",
  ];
  const handleStartSection = () => {
    setSectionStarted((prev) => ({ ...prev, [activeSection]: true }));
  };

  const handleProductComplete = (data) => {
    setProductData(data);
  };

  const handleDemoModeChange = (isDemoMode) => {
    setDemoMode(isDemoMode);
  };

  const handlePlayBookChange = (field, value) => {
    const updatedPlayBookData = { ...playBookData, [field]: value };
    setPlayBookData(updatedPlayBookData);
  };

  // Recalculate cost breakdown when tonnage or OHP rate changes
  const recalculateCostBreakdown = (newTonnage, newOhpRate, originalBreakdown, originalTonnageValue) => {
    if (!originalBreakdown || !originalTonnageValue) {
      return null;
    }

    const to4 = (n) => {
      if (n === null || n === undefined || n === '') return 0;
      const num = typeof n === 'number' ? n : parseFloat(n);
      return isNaN(num) ? 0 : Number(Number(num).toFixed(4));
    };

    // Material_new = Material_old (unchanged)
    const materialNew = to4(originalBreakdown.material);

    // Process_new = Process_old × (New_Tonnage / MachineTonnage_old)
    const processOld = to4(originalBreakdown.process);
    const tonnageRatio = originalTonnageValue > 0 
      ? to4(newTonnage) / to4(originalTonnageValue)
      : 1;
    const processNew = to4(processOld * tonnageRatio);

    // OHP_new = (Material_new + Process_new) × (New_OHP_Rate / 100)
    // OHP rate is a percentage (e.g., 15 means 15%)
    const ohpRateDecimal = to4(newOhpRate) / 100;
    const ohpNew = to4((materialNew + processNew) * ohpRateDecimal);

    // Total_new = Material_new + Process_new + OHP_new
    const totalNew = to4(materialNew + processNew + ohpNew);

    // Calculate percentages
    const materialPercent = totalNew > 0 ? to4((materialNew / totalNew) * 100) : 0;
    const processPercent = totalNew > 0 ? to4((processNew / totalNew) * 100) : 0;
    const ohpPercent = totalNew > 0 ? to4((ohpNew / totalNew) * 100) : 0;

    return {
      material: materialNew,
      process: processNew,
      ohp: ohpNew,
      total_should_cost: totalNew,
      currency: "USD/unit",
      percentages: {
        material: materialPercent,
        process: processPercent,
        ohp: ohpPercent,
      },
      formulas: {
        material: "Material_new = Material_old (unchanged)",
        process: `Process_new = Process_old × (New_Tonnage / MachineTonnage_old) = ${processOld} × (${to4(newTonnage)} / ${to4(originalTonnageValue)}) = ${processNew}`,
        ohp: `OHP_new = (Material_new + Process_new) × (New_OHP_Rate / 100) = (${materialNew} + ${processNew}) × (${to4(newOhpRate)} / 100) = ${ohpNew}`,
        total: `Total_new = Material_new + Process_new + OHP_new = ${materialNew} + ${processNew} + ${ohpNew} = ${totalNew}`,
      },
    };
  };

  // Helper function to analyze cost with explicit data
  const analyzeCostWithData = async (recipe, playbook, product) => {
    try {
      console.log("🚀 Analyzing sheet metal cost with:", { recipe, playbook, product });

      // Map product data to backend parameters
      const operations = product?.operations || {};
      const dimensions = product?.partWeightDimensions || {};
      const materialSelection = product?.materialSelection || "";
      const materialGrade = product?.materialGrade || "";
      let materialType = typeof materialSelection === "object" 
        ? materialSelection.material || materialSelection 
        : materialSelection;
      
      // Transform abbreviations to full names for stored procedure
      const normalizedMaterial = materialType?.trim().toUpperCase() || "";
      if (normalizedMaterial === "CRS") {
        materialType = "Cold Rolled Steel";
      } else if (normalizedMaterial === "SS") {
        materialType = "SS";
      }
      
      // Include material grade in MaterialType if grade is provided
      if (materialGrade && materialGrade.trim() && materialType) {
        materialType = `${materialType.trim()} ${materialGrade.trim()}`.trim();
      }
      
      // Map annual volume to EstimatedVolume format
      const annualVolume = product?.annualVolume || "";
      let estimatedVolume = "Medium volume";
      if (annualVolume.includes("Low") || annualVolume.includes("< 100")) {
        estimatedVolume = "Low volume";
      } else if (annualVolume.includes("High") || annualVolume.includes("> 500")) {
        estimatedVolume = "High volume";
      } else if (annualVolume.includes("Medium") || annualVolume.includes("100,000 - 500,000")) {
        estimatedVolume = "Medium volume";
      }

      // Validate required parameters
      if (!materialType || !product?.manufacturingCountry || !product?.processSelection) {
        throw new Error(
          "Missing required parameters. Please ensure Material, Manufacturing Country, and Process are selected."
        );
      }

      // Prepare stored procedure parameters
      const spParams = {
        TimePerBend_sec: parseFloat(operations.timePerBend) || 0,
        Length_mm: parseFloat(dimensions.partLength) || 0,
        Width_mm: parseFloat(dimensions.partWidth) || 0,
        Thickness_mm: parseFloat(dimensions.partHeight) || 0,
        MaterialType: materialType || "",
        Country: product?.manufacturingCountry || "",
        ProcessType: product?.processSelection || "",
        NoOfOperation: parseInt(operations.operationsCount) || 0,
        DeburringYesNo: operations.deburringEnabled || "No",
        UserFinishWeight_g: parseFloat(dimensions.finishWeight) || 0,
        NoOfBends_PressBrake: parseInt(operations.noOfBendsPressBrake) || 0,
        // NoOfCavities defaults to 1 in backend, not sent from frontend
        NoOfTaps_Tapping: parseInt(operations.noOfTapsHoles) || 0,
        WeldingLength_mm: parseFloat(operations.weldingLength) || 0,
        LabourRateSource: 0,
        EstimatedVolume: estimatedVolume,
      };

      console.log("📞 Calling stored procedure QC_SP_SheetMetal_ with params:", spParams);
      console.log("📤 Request payload:", JSON.stringify(spParams, null, 2));

      // Call the sheet metal costing endpoint
      const costResponse = await apiFetch("/api/costing/sheetmetal", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(spParams),
      });
      
      console.log("📥 Response status:", costResponse.status, costResponse.statusText);
      console.log("📥 Response headers:", Object.fromEntries(costResponse.headers.entries()));

      // Check response
      if (!costResponse.ok) {
        let errorMessage = `Cost analysis failed (${costResponse.status} ${costResponse.statusText})`;
        let errorDetails = null;
        
        // Try to read the response body before throwing error
        const responseClone = costResponse.clone();
        try {
          errorDetails = await responseClone.json();
          console.error("❌ API Error Response (JSON):", JSON.stringify(errorDetails, null, 2));
          errorMessage = errorDetails.error || errorDetails.message || errorMessage;
          // Include full error details in console
          if (errorDetails.error) {
            console.error("❌ Full error details:", errorDetails.error);
          }
        } catch (jsonError) {
          // If response is not JSON, try to get text
          try {
            const errorText = await costResponse.text();
            console.error("❌ API Error Response (text):", errorText);
            errorMessage = errorText || errorMessage;
          } catch (textError) {
            console.error("❌ Failed to read error response:", textError);
          }
        }
        
        // Log the request parameters for debugging
        console.error("❌ Failed request parameters:", JSON.stringify(spParams, null, 2));
        throw new Error(errorMessage);
      }

      // Parse response
      let costResult;
      try {
        costResult = await costResponse.json();
        console.log("✅ Cost analysis complete:", costResult);
      } catch (parseError) {
        console.error("❌ Failed to parse response:", parseError);
        throw new Error("Invalid response format from cost analysis API");
      }
      
      // Check if result is empty
      if (!costResult || (Array.isArray(costResult) && costResult.length === 0)) {
        console.warn("⚠️ Empty result from cost analysis");
        throw new Error("Cost analysis returned no data");
      }
      
      // Extract PlayBook data from cost result
      const costRow = Array.isArray(costResult) && costResult.length > 0 
        ? costResult[0] 
        : (typeof costResult === "object" ? costResult : {});
      
      // Determine which playbook data to use for assumptions:
      // - Use current playbook parameter (contains slider modifications on subsequent runs)
      // - Or initialize from cost result on first run
      let playbookForAssumptions = playbook;
      
      // Helper function to parse numeric values from stored procedure output
      const parseNumericValue = (value, defaultValue = 0) => {
        if (value === null || value === undefined || value === "") return defaultValue;
        const numValue = typeof value === "number" ? value : parseFloat(value);
        return isNaN(numValue) ? defaultValue : numValue;
      };
      
      // Parse values from stored procedure output
      const cycleTimeValue = parseNumericValue(costRow.CycleTime_sec, 0);
      const deburrCycleTimeValue = parseNumericValue(costRow.DeburrCycleTime_sec, 0);
      const pressTonnageValue = parseNumericValue(costRow.MachineTonnage_Tons, 0);
      const ohpRateValue = parseNumericValue(costRow.cMOH_Per || costRow.cOHP_Per, 0);
      
      console.log("🔧 [SheetmetalPage] Stored procedure values:", {
        CycleTime_sec: costRow.CycleTime_sec,
        parsedCycleTime: cycleTimeValue,
        DeburrCycleTime_sec: costRow.DeburrCycleTime_sec,
        parsedDeburrCycleTime: deburrCycleTimeValue,
        MachineTonnage_Tons: costRow.MachineTonnage_Tons,
        parsedPressTonnage: pressTonnageValue,
        cMOH_Per: costRow.cMOH_Per,
        cOHP_Per: costRow.cOHP_Per,
        parsedOhpRate: ohpRateValue,
      });
      
      // Initialize or update PlayBook data from cost result
      // On first run: always use stored procedure values
      // On subsequent runs: update from stored procedure if playbook doesn't exist or if values are 0
      const existingCostParams = playbook?.costParams || {};
      const isFirstRun = !playbook || !playbook.costParams;
      
      const playbookFromResult = {
        costParams: {
          // On first run, always use stored procedure values
          // On subsequent runs, use stored procedure value if current is 0 (not yet set)
          deburrCycleTime: (isFirstRun || existingCostParams.deburrCycleTime === 0) 
            ? deburrCycleTimeValue 
            : existingCostParams.deburrCycleTime,
          cycleTime: (isFirstRun || existingCostParams.cycleTime === 0) 
            ? cycleTimeValue 
            : existingCostParams.cycleTime,
          pressTonnage: (isFirstRun || existingCostParams.pressTonnage === 0) 
            ? pressTonnageValue 
            : existingCostParams.pressTonnage,
          ohpRate: (isFirstRun || existingCostParams.ohpRate === 0) 
            ? ohpRateValue 
            : existingCostParams.ohpRate,
          dieType: existingCostParams.dieType || costRow.ProcessType || "N/A",
        },
      };
      
      console.log("🔧 [SheetmetalPage] Setting playbook data:", {
        isFirstRun,
        playbookFromResult,
        existingCostParams,
        ohpRateInPlaybook: playbookFromResult.costParams.ohpRate,
      });
      
      setPlayBookData(playbookFromResult);
      playbookForAssumptions = playbookFromResult;
      
      // Build assumptions using the current playbook values (slider modifications preserved)
      const enrichedCostResult = enhanceCostResultWithAssumptions(costResult, playbookForAssumptions, product);
      
      // Store original breakdown, tonnage, and OHP rate for recalculation
      // Reset original values when a new cost analysis completes (so we can recalculate from new base)
      if (enrichedCostResult?.breakdown) {
        // Get original OHP rate from stored procedure (cMOH_Per or cOHP_Per)
        const storedOhpRate = costRow.cMOH_Per ?? costRow.cOHP_Per ?? 0;
        
        console.log("💾 [SheetmetalPage] Storing original breakdown:", {
          material: enrichedCostResult.breakdown.material,
          process: enrichedCostResult.breakdown.process,
          ohp: enrichedCostResult.breakdown.ohp,
          total: enrichedCostResult.breakdown.total_should_cost,
          originalOhpRate: storedOhpRate,
          fromStoredProcedure: {
            Overheads_Profits: costRow.Overheads_Profits,
            MaterialCost_USD: costRow.MaterialCost_USD,
            Total_MachinePlusLabour: costRow.Total_MachinePlusLabour,
            cMOH_Per: costRow.cMOH_Per,
            cOHP_Per: costRow.cOHP_Per
          }
        });
        setOriginalCostBreakdown({ ...enrichedCostResult.breakdown });
        setOriginalTonnage(costRow.MachineTonnage_Tons || 0);
        setOriginalOhpRate(storedOhpRate);
      }
      
      setCostData(enrichedCostResult);

      // Handle risk analysis
      // Risk analysis is not required for Sheet Metal; skipping response handling
      // if (riskResponse.ok) {
      //   const riskResult = await riskResponse.json();
      //   console.log("✅ Risk analysis complete:", riskResult);
      //   setRiskData(riskResult);
      // } else {
      //   console.warn("⚠️ Risk analysis failed, continuing without risk data");
      //   setRiskData(null);
      // }

      // Substitute analysis is currently disabled for Sheet Metal
      // if (substituteResponse.ok) {
      //   const substituteResult = await substituteResponse.json();
      //   console.log("✅ Substitute analysis complete:", substituteResult);
      //   setSubstituteData(substituteResult);
      // } else {
      //   console.warn("⚠️ Substitute analysis failed, continuing without substitute data");
      //   setSubstituteData(null);
      // }

      // Generate product image
      await generateProductImageWithData(product, playbookForAssumptions);
      
      setSectionStarted((prev) => ({ ...prev, playbook: true, results: true }));

      // Navigate to results section (PlayBook is now accessible via navigation)
      setActiveSection("results");

      // Fetch key insights using current playbook (preserves user modifications on subsequent runs)
      await fetchKeyInsights(product, playbookForAssumptions);
      await fetchRiskAndSubstituteData(product, playbookForAssumptions);

      return costResult;
    } catch (error) {
      console.error("Analysis failed:", error);
      alert(`Failed to complete analysis: ${error.message}`);
      throw error;
    }
  };

  const handleAnalyzeCost = async () => {
    if (!recipeData || !playBookData || !productData) return;

    setLoadingMessage("Running cost analysis...");
    setIsProcessing(true);
    try {
      await analyzeCostWithData(recipeData, playBookData, productData);
    } finally {
      setLoadingMessage("");
      setIsProcessing(false);
    }
  };

  // Fetch risk and substitute analysis for Analysis Report tabs.
  const fetchRiskAndSubstituteData = async (product, playbook) => {
    if (!product) {
      setRiskData(null);
      setSubstituteData(null);
      return;
    }

    const payload = {
      towerName: "Mechanical",
      costModel: costData?.breakdown || {},
      form: {
        productData: product || {},
        playBookData: playbook || {},
        recipe: recipeData || [],
      },
    };

    const fallbackRecipe = Array.isArray(recipeData) && recipeData.length > 0
      ? recipeData
      : [
          {
            ingredient: product?.materialSelection?.material || product?.materialSelection || "Sheet Metal Material",
            percentage: 100,
            functional_group: "Base",
          },
        ];

    const fnbPayload = {
      recipe: fallbackRecipe,
      playBookData: playbook || {},
      productData: product || {},
    };

    const buildFallbackRisk = () => ({
      summary: `Risk snapshot for ${product?.productName || "sheet metal part"} based on available costing inputs.`,
      top_concerns: [
        "Material price volatility may impact unit cost.",
        "Tooling and setup variability can affect conversion cost.",
        "Process cycle instability may reduce throughput.",
      ],
      risk_items: [
        {
          title: "Material Cost Volatility",
          severity: 6,
          probability: 6,
          detail: "Track metal index and supplier contracts.",
        },
        {
          title: "Process Variability",
          severity: 5,
          probability: 5,
          detail: "Control bend/deburr process window and monitor scrap.",
        },
      ],
      assumptions: "Generated from sheetmetal product and playbook inputs (fallback mode).",
    });

    const buildFallbackSubstitute = () => {
      const params = playbook?.costParams || {};
      const material = product?.materialSelection?.material || product?.materialSelection || "Current material";
      const process = product?.processSelection || "Current process";
      const tonnage = params.pressTonnage || "Current tonnage";
      const cycle = params.cycleTime || "Current cycle time";
      const deburr = params.deburrCycleTime || "Current deburr time";

      return {
        summary: `Sheet metal optimization recommendations generated for ${product?.productName || "the current part"} using material and process inputs.`,
        substitutes: [
          {
            current_item: `Material: ${material}`,
            proposed_item: "Validate equivalent lower-cost grade/material spec",
            estimated_cost_delta_per_unit: "Potential material cost reduction",
            impact_on_quality: "Requires qualification",
            feasibility_score: 6,
            timeline_weeks: 4,
            risk_note: "Verify mechanical properties and tolerance fit.",
            validation_steps: ["Material equivalence review", "Pilot run", "PPAP/quality approval"],
          },
          {
            current_item: `Process setup: ${process}, tonnage: ${tonnage}`,
            proposed_item: "Optimize press parameters and setup sequence",
            estimated_cost_delta_per_unit: "Potential conversion cost reduction",
            impact_on_quality: "Low to medium",
            feasibility_score: 7,
            timeline_weeks: 3,
            risk_note: "Monitor dimensional repeatability after changes.",
            validation_steps: ["Setup trial", "Cycle audit", "Scrap trend verification"],
          },
          {
            current_item: `Cycle: ${cycle}, Deburr: ${deburr}`,
            proposed_item: "Reduce cycle/deburr time through operation balancing",
            estimated_cost_delta_per_unit: "Potential labor/process savings",
            impact_on_quality: "Process-dependent",
            feasibility_score: 7,
            timeline_weeks: 2,
            risk_note: "Validate burr and finish quality after time reduction.",
            validation_steps: ["Time study", "Quality check", "Control plan update"],
          },
        ],
        prioritization: [
          "Operation balancing for immediate cycle reduction",
          "Press/setup optimization for conversion savings",
          "Material equivalence validation for medium-term savings",
        ],
        assumptions: "Generated from sheetmetal product and playbook inputs (fallback mode).",
      };
    };

    try {
      let [riskResponse, substituteResponse] = await Promise.all([
        apiFetch("/api/ai/costing-v4/risk", {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(payload),
        }),
        apiFetch("/api/ai/costing-v4/substitute", {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(payload),
        }),
      ]);

      if (!riskResponse.ok || !substituteResponse.ok) {
        console.warn("costing-v4 risk/substitute failed, falling back to /api/fnb endpoints");
        [riskResponse, substituteResponse] = await Promise.all([
          apiFetch("/api/fnb/analyze-risk", {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify(fnbPayload),
          }),
          apiFetch("/api/fnb/find-substitutes", {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify(fnbPayload),
          }),
        ]);
      }

      if (riskResponse.ok) {
        const riskResult = await riskResponse.json();
        setRiskData(riskResult);
      } else {
        const riskErr = await riskResponse.json().catch(() => ({}));
        console.warn("Risk analysis failed:", riskErr);
        setRiskData(buildFallbackRisk());
      }

      if (substituteResponse.ok) {
        const substituteResult = await substituteResponse.json();
        setSubstituteData(substituteResult);
      } else {
        const substituteErr = await substituteResponse.json().catch(() => ({}));
        console.warn("Substitute analysis failed:", substituteErr);
        setSubstituteData(buildFallbackSubstitute());
      }
    } catch (error) {
      console.error("Risk/Substitute analysis failed:", error);
      setRiskData(buildFallbackRisk());
      setSubstituteData(buildFallbackSubstitute());
    }
  };

  const fetchKeyInsights = async (product, playbook) => {
    if (!product) {
      return;
    }
    try {
      const response = await apiFetch("/api/mech/key-insights-sheetmetal", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          productData: product,
          playBookData: playbook || {},
        }),
      });
      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(errorText || "Failed to generate key insights");
      }
      const insights = await response.json();
      setKeyInsightsData(insights);
    } catch (error) {
      console.error("Failed to fetch key insights:", error);
    }
  };

  // Recalculate cost breakdown when tonnage or OHP rate changes
  useEffect(() => {
    // Only recalculate if we have all required data
    if (!costData?.breakdown || !playBookData?.costParams || !originalCostBreakdown || !originalTonnage || originalOhpRate === null) {
      return;
    }

    const costParams = playBookData.costParams;
    const newTonnage = costParams.pressTonnage;
    const newOhpRate = costParams.ohpRate;

    // Use current slider values or original values as fallback
    const tonnageToUse = newTonnage !== undefined && newTonnage !== null ? newTonnage : originalTonnage;
    
    // Check if values have actually changed from the stored procedure values
    const tonnageChanged = newTonnage !== undefined && newTonnage !== null && 
                          Math.abs(newTonnage - originalTonnage) > 0.01;
    
    // OHP rate has changed only if user explicitly set a different value from stored procedure
    const ohpRateChanged = newOhpRate !== undefined && newOhpRate !== null && 
                           Math.abs(newOhpRate - originalOhpRate) > 0.01;

    // If nothing has changed, don't recalculate
    if (!tonnageChanged && !ohpRateChanged) {
      return;
    }

    // If only tonnage changed, keep original OHP value
    // If OHP rate changed, recalculate OHP
    const ohpRateToUse = ohpRateChanged ? newOhpRate : originalOhpRate;

    console.log("🔄 [SheetmetalPage] Recalculating breakdown:", {
      originalOhp: originalCostBreakdown.ohp,
      originalOhpRate,
      newOhpRate,
      ohpRateToUse,
      tonnageChanged,
      ohpRateChanged,
      willRecalculateOHP: ohpRateChanged
    });

    // Recalculate breakdown
    const recalculated = recalculateCostBreakdown(
      tonnageToUse,
      ohpRateToUse,
      originalCostBreakdown,
      originalTonnage
    );

    if (recalculated && costData.breakdown) {
      // Only update if values actually changed to avoid infinite loops
      const currentTotal = costData.breakdown.total_should_cost || 0;
      const newTotal = recalculated.total_should_cost || 0;
      
      if (Math.abs(currentTotal - newTotal) > 0.0001) {
        console.log("✅ [SheetmetalPage] Updating breakdown with recalculated values:", {
          originalOhp: originalCostBreakdown.ohp,
          recalculatedOhp: recalculated.ohp,
          ohpChanged: ohpRateChanged
        });
        // Update costData with recalculated breakdown
        // If OHP rate didn't change, preserve the original OHP value
        const to4 = (n) => {
          if (n === null || n === undefined || n === '') return 0;
          const num = typeof n === 'number' ? n : parseFloat(n);
          return isNaN(num) ? 0 : Number(Number(num).toFixed(4));
        };
        
        const finalOhp = ohpRateChanged ? recalculated.ohp : originalCostBreakdown.ohp;
        const finalTotal = ohpRateChanged 
          ? recalculated.total_should_cost 
          : to4(recalculated.material + recalculated.process + originalCostBreakdown.ohp);
        
        // Rebuild assumptions with updated breakdown
        const updatedBreakdown = {
          ...costData.breakdown,
          material: recalculated.material,
          process: recalculated.process,
          ohp: finalOhp,
          total_should_cost: finalTotal,
          currency: "USD/unit",
        };
        
        // Get the original cost row for assumptions (we need it to rebuild assumptions)
        const costRow = costData._originalCostRow || {};
        const updatedAssumptions = buildSheetmetalAssumptions(
          costRow,
          playBookData,
          productData,
          updatedBreakdown
        );
        
        setCostData(prev => ({
          ...prev,
          breakdown: updatedBreakdown,
          assumptions: updatedAssumptions,
          summary: `Total cost: $${finalTotal.toFixed(4)} per unit`,
          _recalculated: true,
          _recalculationFormulas: recalculated.formulas,
        }));
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [playBookData?.costParams?.pressTonnage, playBookData?.costParams?.ohpRate, originalCostBreakdown, originalTonnage, originalOhpRate]);

  const generateProductImageWithData = async (product, playbook) => {
    if (!product) return;

    setGeneratingImage(true);

    try {
      const imagePayload = {
        productName: product.productName || "",
        materialSelection:
          product.materialSelection?.material ||
          product.materialSelection?.name ||
          product.materialSelection ||
          "",
        processSelection: product.processSelection || "",
        partWeightDimensions: product.partWeightDimensions || {},
        companyName: "Kearney",
        brandName: "PERLab",
        commodity: "Sheet Metal",
        primaryPackaging: playbook?.primary_packaging || "",
        style: "professional",
      };

      console.log("🖼️ Generating product image:", imagePayload);

      // const response = await apiFetch("/api/ai/generate-product-image", {
      const response = await apiFetch("/api/mech/sheetmetal/generate-image", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(imagePayload),
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || "Failed to generate image");
      }

      const data = await response.json();
      setProductImageUrl(data.image_url);
      console.log("✅ Product image generated:", data.image_url);
    } catch (error) {
      console.error("❌ Error generating product image:", error);
      // Don't alert - image is optional
    } finally {
      setGeneratingImage(false);
    }
  };

  const generateProductImage = () => {
    return generateProductImageWithData(productData, playBookData);
  };

  const handleProgressiveQuickCost = async () => {
    if (!productData) {
      alert("Please fill in product information first");
      return;
    }

    console.log("Starting Progressive Quick Cost Analysis for Sheet Metal...");

    // Initialize progress steps immediately with descriptive labels
    const initialSteps = [
      { id: "step-1", label: "Documenting Assumptions", status: "in-progress", message: "Processing..." },
      { id: "step-2", label: "Analyzing Material Requirements", status: "pending", message: "" },
      { id: "step-3", label: "Calculating Process Costs", status: "pending", message: "" },
      { id: "step-4", label: "Computing Machine Parameters", status: "pending", message: "" },
      { id: "step-5", label: "Generating Cost Breakdown", status: "pending", message: "" },
      { id: "step-6", label: "Finalizing Analysis Report", status: "pending", message: "" },
    ];
    setProgressSteps(initialSteps);
    setCurrentProgressStep(1);
    setOverallProgress(0);
    setIsProcessing(true);
    setShowProgressTracker(true);
    setKeyInsightsData(null);
    setRiskData(null);
    setSubstituteData(null);

    try {
      // Step 1: Documenting Assumptions (16.67%)
      setProgressSteps(prev => prev.map((step, idx) => 
        idx === 0 ? { ...step, status: "in-progress", message: "Processing..." } : step
      ));
      setCurrentProgressStep(1);
      setOverallProgress(16.67);
      
      await new Promise(resolve => setTimeout(resolve, 500)); // Simulate work

      // Step 2: Analyzing Material Requirements (33.33%)
      setProgressSteps(prev => prev.map((step, idx) => 
        idx === 0 ? { ...step, status: "completed", message: "Complete" } :
        idx === 1 ? { ...step, status: "in-progress", message: "Processing..." } : step
      ));
      setCurrentProgressStep(2);
      setOverallProgress(33.33);

      await new Promise(resolve => setTimeout(resolve, 500)); // Simulate work

      // Step 3: Calculating Process Costs (50%) - Call analysis here
      setProgressSteps(prev => prev.map((step, idx) => 
        idx === 1 ? { ...step, status: "completed", message: "Complete" } :
        idx === 2 ? { ...step, status: "in-progress", message: "Processing..." } : step
      ));
      setCurrentProgressStep(3);
      setOverallProgress(50);

      // Call the analysis with current playbook data
      console.log("🚀 Running Quick Cost analysis with:", productData);
      await analyzeCostWithData(null, playBookData || null, productData);

      // Step 4: Computing Machine Parameters (66.67%)
      setProgressSteps(prev => prev.map((step, idx) => 
        idx === 2 ? { ...step, status: "completed", message: "Complete" } :
        idx === 3 ? { ...step, status: "in-progress", message: "Processing..." } : step
      ));
      setCurrentProgressStep(4);
      setOverallProgress(66.67);

      await new Promise(resolve => setTimeout(resolve, 500)); // Simulate work

      // Step 5: Generating Cost Breakdown (83.33%)
      setProgressSteps(prev => prev.map((step, idx) => 
        idx === 3 ? { ...step, status: "completed", message: "Complete" } :
        idx === 4 ? { ...step, status: "in-progress", message: "Processing..." } : step
      ));
      setCurrentProgressStep(5);
      setOverallProgress(83.33);

      await new Promise(resolve => setTimeout(resolve, 500)); // Simulate work

      // Step 6: Finalizing Analysis Report (100%)
      setProgressSteps(prev => prev.map((step, idx) => 
        idx === 4 ? { ...step, status: "completed", message: "Complete" } :
        idx === 5 ? { ...step, status: "in-progress", message: "Processing..." } : step
      ));
      setCurrentProgressStep(6);
      setOverallProgress(100);

      // Mark all complete
      setProgressSteps(prev => prev.map(step => ({ ...step, status: "completed", message: "Complete" })));

      // Close dialog
      setShowProgressTracker(false);
      setIsProcessing(false);

      console.log("✅ Progressive Quick Cost completed successfully!");
    } catch (error) {
      console.error("❌ Progressive Quick Cost failed:", error);
      alert(`Failed to complete analysis: ${error.message}`);
      setIsProcessing(false);
      setShowProgressTracker(false);
    }
  };

  const handleQuickCost = async () => {
    // Use progressive tracker for better UX
    return handleProgressiveQuickCost();

    /* Original implementation kept for reference
    if (!productData) return;

    setLoadingMessage("Running cost analysis...");
    setIsProcessing(true);
    setKeyInsightsData(null);

    try {
      console.log("🚀 Running Quick Cost analysis with:", productData);

      // Pass current playBookData if it exists (on subsequent runs), otherwise null (first run)
      await analyzeCostWithData(null, playBookData || null, productData);
    } catch (error) {
      console.error("Quick Cost failed:", error);
      alert(`Failed to run cost analysis: ${error.message}`);
    } finally {
      setLoadingMessage("");
      setIsProcessing(false);
    }
    */
  };

  const navItems = [
    {
      id: "product",
      label: "Product Information",
      icon: <DescriptionIcon />,
      disabled: false,
      title: "Build Product Profile",
      description:
        "Answer a few quick questions about your product to generate an AI-driven cost analysis with  manufacturing parameters, and detailed insights.",
    },
    // {
    //   id: "formulation",
    //   label: "Formulation",
    //   icon: <ScienceIcon />,
    //   disabled: false,
    //   title: "Ready to Build Formulation",
    //   description: "View and edit ingredient composition with functional groups and percentages.",
    // },
    {
      id: "playbook",
      label: "PlayBook",
      icon: <TuneIcon />,
      disabled: !sectionStarted.product, // Disabled until user starts product section
      title: "Ready to Configure PlayBook",
      description:
        "Configure manufacturing parameters including moisture content, process loss, packaging loss, overhead percentages, and packaging details.",
    },
    {
      id: "results",
      label: "Analysis Report",
      icon: <AssessmentIcon />,
      disabled: !sectionStarted.product, // Disabled until user starts product section
      title: "Analysis Report",
      description:
        "Access detailed cost breakdown, risk analysis, and substitute recommendations with visual charts and insights.",
    },
  ];

const costRows = useMemo(() => buildCostRows(costData?.breakdown || {}), [costData]);
 
  const costTotal = useMemo(() => {
    const declaredTotal =
      costData?.breakdown?.total_should_cost ?? costData?.breakdown?.total;
    const declaredNumber = Number(declaredTotal);
    if (Number.isFinite(declaredNumber)) return declaredNumber;
    console.log(costRows)
    return costRows.reduce((sum, row) => sum + (Number(row.value) || 0), 0);
  }, [costData, costRows]);

  return (
    <SidebarLayout>
      <Box
        sx={{
          display: "grid",
          gridTemplateRows: "auto 1fr",
          height: "100%",
          width: "100%",
          overflow: "hidden",
          bgcolor: perlColors.background,
          color: perlColors.textPrimary,
        }}
      >
        <TitleSection
          towerName="Mechanical"
          industry="Mechanical"
          commodity="Sheet Metal"
          tierImage="/images/Silver.jpg"
          tierLabel="Agentic Lite"
          showAI={false}
        />

        <Box
          sx={{
            display: "flex",
            height: "100%",
            overflow: "hidden",
          }}
        >
          <LeftNavigation
            navItems={navItems}
            activeSection={activeSection}
            onSectionChange={setActiveSection}
            showTooltips={true}
          />

          <Box
            sx={{
              flex: 1,
              overflow: "auto",
              height: "100%",
              p: perlSpacing.mainPadding || 3,
              "&::-webkit-scrollbar": { width: "8px" },
              "&::-webkit-scrollbar-thumb": {
                bgcolor: perlColors.border,
                borderRadius: "4px",
              },
            }}
          >
            {/* Show info notification only for product section when not started */}
            {!sectionStarted.product && activeSection === "product" && (
              <InfoNotification
                title={navItems.find((item) => item.id === activeSection)?.title}
                message={navItems.find((item) => item.id === activeSection)?.description}
                show={true}
                showButton={true}
                onButtonClick={handleStartSection}
              />
            )}

            {/* Keep SheetmetalQuestions mounted but hidden when not active to preserve state */}
            {sectionStarted.product && (
              <Box sx={{ display: activeSection === "product" ? "block" : "none",height:"92%" }}>
                <SheetmetalQuestions
                  onComplete={handleProductComplete}
                  onDataChange={setProductData}
                  onDemoModeChange={handleDemoModeChange}
                  initialData={productData}
                />
              </Box>
            )}


            {activeSection === "playbook" && sectionStarted.playbook && (
              <Box>
                {playBookData ? (
                  <SheetmetalPlayBook 
                    data={playBookData} 
                    onChange={handlePlayBookChange}
                    costBreakdown={costData?.breakdown}
                  />
                ) : (
                  <Typography sx={{ color: perlColors.textSecondary }}>
                    Generate recipe first to load PlayBook data
                  </Typography>
                )}
              </Box>
            )}

            {activeSection === "results" && sectionStarted.results && (
              <Box>
                {costData ? (
                  <ResultsSection
                    costData={costData}
                    costRows={costRows}
                    costTotal={costTotal}
                    riskData={riskData}
                    substituteData={substituteData}
                    productImageUrl={productImageUrl}
                    generatingImage={generatingImage}
                    onRegenerateImage={generateProductImage}
                    productName={productData?.productName}
                    productData={productData}
                    brandColor="#ff6034"
                    assumptions={costData?.assumptions || []}
                    keyInsights={keyInsightsData}
                    substituteSectionTitle="Sheet Metal Recommendations"
                    onNavigateToPlayBook={() => {
                      setSectionStarted((prev) => ({ ...prev, playbook: true }));
                      setActiveSection("playbook");
                    }}
                    renderedAssumptions={renderAssumptions({
                      assumptions: costData?.assumptions || [],
                      productData,
                      perlColors,
                      perlTypography: theme.custom?.perlTypography || {},
                    })}
                  />
                ) : (
                  <Typography sx={{ color: perlColors.textSecondary }}>
                    Run Quick Cost to generate analysis report
                  </Typography>
                )}
              </Box>
            )}
          </Box>
        </Box>

        {/* Quick Cost Button - Show only on product section when data is available */}
        {sectionStarted.product &&  (
          <QuickCostButton
            onClick={handleQuickCost}
            disabled={!productData}
            loading={isProcessing}
            brandColor="#ff6034"
          />
        )}

        {/* Progressive Analysis Tracker Dialog */}
        <Dialog
          open={showProgressTracker}
          maxWidth={false}
          disableEscapeKeyDown
          PaperProps={{
            sx: {
              backgroundColor: "transparent",
              boxShadow: "none",
              borderRadius: 4,
            },
          }}
          BackdropProps={{
            sx: {
              backgroundColor: "rgba(0, 0, 0, 0.5)",
            },
          }}
        >
          <DialogContent sx={{ p: 0 }}>
            <ProgressiveAnalysisTracker
              steps={progressSteps}
              currentStepNumber={currentProgressStep}
              overallProgress={overallProgress}
            />
          </DialogContent>
        </Dialog>

        {/* Legacy Circular Loading (kept for backward compatibility) */}
        <CircularLoading
          open={isProcessing && !showProgressTracker}
          message={loadingMessage || "Running quick cost..."}
          subtext={productData?.productName ? `Product: ${productData.productName}` : undefined}
        />
      </Box>
    </SidebarLayout>
  );
}
